import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaInfoCircle,
  FaExclamationTriangle,
  FaExclamationCircle,
  FaBell,
  FaBullhorn,
} from 'react-icons/fa';
import { FaRegClock, FaRegCalendarDays, FaCloudShowersHeavy, FaSnowman, FaSnowflake } from 'react-icons/fa6';
import { TbSnowman } from "react-icons/tb";
import SafeHTML from '../common/SafeHTML';
import {
  FiChevronUp,
  FiChevronDown,
  FiPlay,
  FiPause,
} from 'react-icons/fi';
import { apiService } from '../../services/api';

const ICON_MAP = {
  'info-circle': FaInfoCircle,
  'exclamation-triangle': FaExclamationTriangle,
  'exclamation-circle': FaExclamationCircle,
  'bell': FaBell,
  'bullhorn': FaBullhorn,
  'clock': FaRegClock,
  'calendar': FaRegCalendarDays,
  'rain-heavy': FaCloudShowersHeavy,
  'snowman': FaSnowman,
  'snowman-outline': TbSnowman,
  'snowflake': FaSnowflake,
};

const BG_COLOR_MAP = {
  alert: 'var(--color-alert)',
  primary: 'var(--color-primary)',
  secondary: 'var(--color-secondary)',
  accent: 'var(--color-accent)',
  highlight: 'var(--color-highlight)',
  neutral: 'var(--color-neutral)',
};

export default function AlertBanner() {
  const [banners, setBanners] = useState([]);
  const [settings, setSettings] = useState({
    alert_banner_timer: 5,
    alert_banner_text_color: 'white',
    alert_banner_bg_color: 'primary',
    alert_banner_enabled: true,
  });
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const timerRef = useRef(null);
  const bannerRef = useRef(null);

  // Fetch banners on mount
  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const response = await apiService.getAlertBanners();
        if (response.data && response.data.length > 0) {
          setBanners(response.data);
        }
        if (response.settings) {
          setSettings(response.settings);
        }
      } catch (err) {
        // Silently fail - banner is non-critical
      } finally {
        setLoaded(true);
      }
    };
    fetchBanners();
  }, []);

  // Set CSS variable for banner height
  useEffect(() => {
    if (banners.length === 0) {
      document.documentElement.style.setProperty('--alert-banner-height', '0px');
      return;
    }
    const updateHeight = () => {
      const h = bannerRef.current ? `${bannerRef.current.offsetHeight}px` : '36px';
      document.documentElement.style.setProperty('--alert-banner-height', h);
    };
    updateHeight();
    // Re-measure after framer-motion transition
    const timeout = setTimeout(updateHeight, 350);
    return () => {
      clearTimeout(timeout);
      document.documentElement.style.setProperty('--alert-banner-height', '0px');
    };
  }, [banners.length, expanded]);

  // Auto-advance timer
  const startTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (banners.length <= 1) return;
    const interval = (settings.alert_banner_timer || 5) * 1000;
    timerRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % banners.length);
    }, interval);
  }, [banners.length, settings.alert_banner_timer]);

  useEffect(() => {
    if (isPlaying && banners.length > 1 && !expanded) {
      startTimer();
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isPlaying, startTimer, banners.length, expanded]);

  const goNext = () => {
    setExpanded(false);
    setCurrentIndex((prev) => (prev + 1) % banners.length);
    if (isPlaying) startTimer();
  };

  const goPrev = () => {
    setExpanded(false);
    setCurrentIndex((prev) => (prev - 1 + banners.length) % banners.length);
    if (isPlaying) startTimer();
  };

  const togglePlay = () => setIsPlaying((prev) => !prev);

  const toggleExpand = () => setExpanded((prev) => !prev);

  // Don't render if no banners or disabled or not loaded
  if (!loaded || banners.length === 0 || !settings.alert_banner_enabled) {
    return null;
  }

  const current = banners[currentIndex];
  const IconComponent = ICON_MAP[current?.icon] || FaInfoCircle;

  // Resolve background color
  const bgColor = BG_COLOR_MAP[settings.alert_banner_bg_color] || settings.alert_banner_bg_color;
  const textColor = settings.alert_banner_text_color === 'black' ? '#000000' : '#FFFFFF';

  return (
    <div
      ref={bannerRef}
      className="fixed top-0 left-0 right-0 z-[60] transition-[height] duration-300"
      style={{ backgroundColor: bgColor, color: textColor }}
    >
      <div className="max-w-7xl mx-auto w-full px-4 flex items-start justify-center relative min-h-[36px] py-2">
        {/* Carousel content - clickable to expand */}
        <button
          onClick={toggleExpand}
          className="flex-1 flex items-center justify-center overflow-hidden min-w-0 min-h-[36px] cursor-pointer text-left"
          aria-label={expanded ? 'Collapse alert details' : 'Expand alert details'}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={`${currentIndex}-${expanded}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3 }}
              className={expanded
                ? 'flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-sm py-2'
                : 'flex items-center gap-2 text-sm whitespace-nowrap overflow-hidden'
              }
            >
              <span className="flex items-center gap-2 flex-shrink-0">
                <IconComponent className="w-5 h-5 flex-shrink-0" />
                {current?.title && (
                  <span className="font-semibold">{current.title}</span>
                )}
              </span>
              {current?.content && (
                <SafeHTML
                  html={current.content}
                  as="span"
                  className={expanded
                    ? 'opacity-90 [&>*]:inline'
                    : 'opacity-90 truncate [&>*]:inline'
                  }
                />
              )}
            </motion.div>
          </AnimatePresence>
        </button>

        {/* Controls - only show if multiple banners */}
        {banners.length > 1 && (
          <div className="flex items-center gap-0.5 ml-3 flex-shrink-0 h-[36px]">
            <button
              onClick={goPrev}
              className="p-0.5 rounded opacity-50 hover:opacity-100 transition-opacity"
              aria-label="Previous alert"
            >
              <FiChevronUp className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={togglePlay}
              className="p-0.5 rounded opacity-50 hover:opacity-100 transition-opacity"
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? (
                <FiPause className="w-3 h-3" />
              ) : (
                <FiPlay className="w-3 h-3" />
              )}
            </button>
            <button
              onClick={goNext}
              className="p-0.5 rounded opacity-50 hover:opacity-100 transition-opacity"
              aria-label="Next alert"
            >
              <FiChevronDown className="w-3.5 h-3.5" />
            </button>
            <span className="text-[10px] opacity-40 ml-1 tabular-nums">
              {currentIndex + 1}/{banners.length}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
